/**
 * useDocumentComments — Real-time Firestore listener for document comments (v2)
 *
 * Subscribes to `patients/{patientId}/documents/{documentId}/comments`,
 * groups by section, and exposes CRUD ops. Resolution and threading
 * removed in v2 (margin-based inline comments).
 *
 * See: docs/features/document-system/document-comments.md
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../utils/firebase";
import { getData } from "../utils/firestoreService";
import { logAuditEventAsync, AuditActions } from "../utils/auditLogger";

export function useDocumentComments({ patientId, documentId, enabled = true, readOnly = false }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const collectionPath = `patients/${patientId}/documents/${documentId}/comments`;
  const accessRef = useRef();
  accessRef.current = { collectionPath, writable: !!patientId && !!documentId && enabled && !readOnly };
  const canWrite = useCallback(() => accessRef.current.writable &&
    accessRef.current.collectionPath === collectionPath, [collectionPath]);

  // Cache the current user's doc (role, displayName) to avoid re-fetching per comment
  const userDocRef = useRef(null);
  const userDocFetchedForRef = useRef(null);

  const getUserDoc = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return null;
    if (userDocFetchedForRef.current === uid && userDocRef.current) {
      return userDocRef.current;
    }
    try {
      const userDoc = await getData(`users/${uid}`);
      userDocRef.current = userDoc;
      userDocFetchedForRef.current = uid;
      return userDoc;
    } catch (err) {
      console.error("[useDocumentComments] Error fetching user doc:", err);
      return null;
    }
  }, []);

  // ---------- Real-time listener ----------
  useEffect(() => {
    if (!patientId || !documentId || !enabled) {
      setComments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const commentsRef = collection(
      db,
      `patients/${patientId}/documents/${documentId}/comments`
    );
    const q = query(commentsRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setComments(items);
        setLoading(false);
      },
      (error) => {
        console.error("[useDocumentComments] Snapshot error:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [patientId, documentId, enabled]);

  // ---------- Derived state ----------
  const activeComments = useMemo(
    () => comments.filter((c) => !c.deleted && !c.parentCommentId),
    [comments]
  );

  const commentsBySection = useMemo(() => {
    const sectionMap = new Map();
    activeComments.forEach((c) => {
      const key = c.sectionKey || "general";
      if (!sectionMap.has(key)) sectionMap.set(key, []);
      sectionMap.get(key).push(c);
    });
    return sectionMap;
  }, [activeComments]);

  const commentCount = activeComments.length;

  // ---------- CRUD ----------

  const addComment = useCallback(
    async (body, sectionKey = null, anchor = null) => {
      const user = auth.currentUser;
      if (!user || !canWrite()) return null;

      const userDoc = await getUserDoc();
      if (!canWrite() || auth.currentUser?.uid !== user.uid) return null;

      // Separate text anchors from row anchors via type discriminator
      const isRowAnchor = anchor?.type === "row";

      const commentData = {
        body,
        sectionKey: sectionKey || null,
        anchor: isRowAnchor ? null : (anchor || null),
        rowAnchor: isRowAnchor ? { rowId: anchor.rowId, rowLabel: anchor.rowLabel } : null,
        parentCommentId: null,
        authorId: user.uid,
        authorName: user.displayName || userDoc?.displayName || "Unknown",
        authorRole: userDoc?.role || "standard",
        createdAt: serverTimestamp(),
        updatedAt: null,
        deleted: false,
      };

      try {
        const docRef = await addDoc(
          collection(db, collectionPath),
          commentData
        );
        logAuditEventAsync(AuditActions.COMMENT_CREATE, {
          resourceType: "comment",
          resourceId: docRef.id,
          patientId,
          metadata: { documentId, sectionKey, anchor },
        });
        return docRef.id;
      } catch (error) {
        console.error("[useDocumentComments] addComment error:", error);
        return null;
      }
    },
    [patientId, documentId, collectionPath, getUserDoc, canWrite]
  );

  const editComment = useCallback(
    async (commentId, newBody) => {
      if (!canWrite() || !commentId || !newBody) return false;
      try {
        const commentDoc = doc(db, collectionPath, commentId);
        await updateDoc(commentDoc, {
          body: newBody,
          updatedAt: serverTimestamp(),
        });
        logAuditEventAsync(AuditActions.COMMENT_EDIT, {
          resourceType: "comment",
          resourceId: commentId,
          patientId,
          metadata: { documentId },
        });
        return true;
      } catch (error) {
        console.error("[useDocumentComments] editComment error:", error);
        return false;
      }
    },
    [collectionPath, patientId, documentId, canWrite]
  );

  const deleteComment = useCallback(
    async (commentId) => {
      if (!canWrite() || !commentId) return false;
      try {
        const commentDoc = doc(db, collectionPath, commentId);
        await updateDoc(commentDoc, { deleted: true });
        logAuditEventAsync(AuditActions.COMMENT_DELETE, {
          resourceType: "comment",
          resourceId: commentId,
          patientId,
          metadata: { documentId },
        });
        return true;
      } catch (error) {
        console.error("[useDocumentComments] deleteComment error:", error);
        return false;
      }
    },
    [collectionPath, patientId, documentId, canWrite]
  );

  // Batch-update anchor positions (called on save to sync drift)
  const updateAnchors = useCallback(
    async (anchorUpdates) => {
      // anchorUpdates: Array<{ commentId, startOffset, endOffset, textSnippet }>
      if (!canWrite() || !anchorUpdates?.length) return;
      try {
        await Promise.all(
          anchorUpdates.map(({ commentId, startOffset, endOffset, textSnippet }) => {
            const commentDoc = doc(db, collectionPath, commentId);
            return updateDoc(commentDoc, {
              "anchor.startOffset": startOffset,
              "anchor.endOffset": endOffset,
              "anchor.textSnippet": textSnippet,
            });
          })
        );
      } catch (error) {
        console.error("[useDocumentComments] updateAnchors error:", error);
      }
    },
    [collectionPath, canWrite]
  );

  // Promote an anchored comment to section-level (orphaned anchor)
  const promoteToSectionComment = useCallback(
    async (commentId) => {
      if (!canWrite() || !commentId) return;
      try {
        const commentDoc = doc(db, collectionPath, commentId);
        await updateDoc(commentDoc, { anchor: null });
      } catch (error) {
        console.error("[useDocumentComments] promoteToSectionComment error:", error);
      }
    },
    [collectionPath, canWrite]
  );

  return {
    comments: activeComments,
    commentsBySection,
    commentCount,
    loading,
    addComment,
    editComment,
    deleteComment,
    updateAnchors,
    promoteToSectionComment,
  };
}
